import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import AppShell from './shell/AppShell';
import RequireAuth from './shell/RequireAuth';
import Home from './screens/Home';
import MyApps from './screens/MyApps';
import Account from './screens/Account';
import Credits from './screens/Credits';
import { BillingCancelled, BillingSuccess } from './screens/billing/BillingReturn';
import SignIn from './screens/auth/SignIn';
import SignUp from './screens/auth/SignUp';
import Confirm from './screens/auth/Confirm';
import AuthCallback from './screens/auth/AuthCallback';
import ForgotPassword from './screens/auth/ForgotPassword';
import Intent from './screens/new/Intent';
import Idea from './screens/new/Idea';
import Plan from './screens/new/Plan';
import ProjectEditor from './screens/project/ProjectEditor';
import AppDetail from './screens/app/AppDetail';
import Members from './screens/app/Members';
import Users from './screens/app/Users';
import Broadcast from './screens/app/Broadcast';
import Invite from './screens/Invite';
import Creator from './screens/Creator';
import Submissions from './screens/Submissions';
import AdminMetrics from './screens/AdminMetrics';
import NotFound from './screens/NotFound';

const guarded = (el: ReactNode) => <RequireAuth>{el}</RequireAuth>;
/**
 * The journey is OPEN until Build (owner, 2026-10-03). It asked for an account before the idea
 * (aec2c7c), and the first day of ads showed the cost: 10 visitors saw "Create an account", none
 * made one, and none saw a plan. A signed-out visitor now describes the app, gets a tailored plan
 * (capped server-side, webBuild.ts `anonymousPlan`) and walks the steps; the account is asked for
 * at Build — and at the photo step, since uploads need one — and they come back to the review.
 */
const journey = (el: ReactNode) => el;

/**
 * Real URLs — the one place the web should beat the phone, since a project becomes
 * linkable. Expo Router's `(group)` segments have no equivalent here; the router shim
 * strips them when shared code navigates.
 */
export function AppRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Home />} />
        {/* Discover is switched off for now. A saved link or an old share may still point here,
            so it lands on Build — replacing itself, so Back does not walk into the redirect
            again. The server's catalogue routes are unchanged. */}
        <Route path="/discover" element={<Navigate to="/" replace />} />
        <Route path="/my-apps" element={guarded(<MyApps />)} />
        <Route path="/account" element={guarded(<Account />)} />
        <Route path="/credits" element={guarded(<Credits />)} />
        {/* Stripe's return pages. Unguarded: they grant nothing, and a paying customer
            whose session lapsed during checkout must still be told it worked. */}
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancelled" element={<BillingCancelled />} />

        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/confirm" element={<Confirm />} />
        {/* Google and Apple return here, through Cognito (api/oauth.ts). */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route path="/new" element={journey(<Intent />)} />
        <Route path="/new/idea" element={journey(<Idea />)} />
        <Route path="/new/plan" element={journey(<Plan />)} />

        <Route path="/project/:id" element={guarded(<ProjectEditor />)} />
        <Route path="/project/:id/preview" element={guarded(<ProjectEditor mode="preview" />)} />

        <Route path="/app/:appId" element={guarded(<AppDetail />)} />
        <Route path="/members/:appId" element={guarded(<Members />)} />
        <Route path="/users/:appId" element={guarded(<Users />)} />
        <Route path="/broadcast/:appId" element={guarded(<Broadcast />)} />
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/creator/:id" element={<Creator />} />
        {/* Replies to a published app. Media-asset management (the phone's AssetsPanel)
            is not ported yet — it edits an app's uploaded files, not its replies. */}
        <Route path="/submissions/:id" element={guarded(<Submissions />)} />
        <Route path="/admin/metrics" element={guarded(<AdminMetrics />)} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}
